import type { APIErrorBody } from "./types";

const TOKEN_KEY = "keymarket.session";

export class APIError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = "APIError";
    this.status = status;
    this.code = code;
  }
}

function localizedAPIMessage(code: string, message: string): string {
	const exact: Record<string, string> = {
		email_verification_required: "请先完成邮箱验证，新的验证邮件已进入发送队列。",
		mfa_required: "该操作需要多因素认证，请先在账户安全中启用并完成 MFA。",
		review_evidence_failed: "当前审核证据不满足通过条件，请核对邮箱验证和自动验钥结果。",
		balance_adjustment_conflict: "该到账编号已存在且信息不一致，请核对后重试。",
		payment_state_conflict: "该充值单已到账、已作废或不是对公人工订单，不能执行作废。",
		permission_denied: "当前账号没有执行该操作的权限。",
	};
	if (exact[code]) return exact[code];
	if (/amount must be positive/i.test(message)) return "金额必须大于 0。";
	if (/CNY supports at most 2 decimal places/i.test(message)) return "人民币金额最多保留两位小数。";
	if (/balance adjustment request conflicts/i.test(message)) return "余额调整单与已有到账记录冲突，请核对到账编号、金额和币种。";
	return message;
}

export function getSessionToken(): string {
  return sessionStorage.getItem(TOKEN_KEY) ?? "";
}

export function setSessionToken(token: string): void {
  if (token) sessionStorage.setItem(TOKEN_KEY, token);
  else sessionStorage.removeItem(TOKEN_KEY);
  window.dispatchEvent(new CustomEvent("keymarket:session"));
}

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  const token = getSessionToken();
  if (token) headers.set("Authorization", `Bearer ${token}`);
  headers.set("Accept", "application/json");
  headers.set("X-Request-ID", crypto.randomUUID());
  if (init.body && !(init.body instanceof FormData)) headers.set("Content-Type", "application/json");
  const response = await fetch(path, { ...init, headers, credentials: "same-origin" });
  if (response.status === 204) return undefined as T;
  const contentType = response.headers.get("content-type") ?? "";
  const payload = contentType.includes("application/json")
    ? ((await response.json()) as unknown)
    : await response.text();
  if (!response.ok) {
    const body = (typeof payload === "object" && payload ? payload : {}) as APIErrorBody;
    const code = body.error?.code ?? `http_${response.status}`;
		const message = localizedAPIMessage(code, body.error?.message ?? (typeof payload === "string" ? payload : "请求失败"));
    if (response.status === 401 && token) setSessionToken("");
    throw new APIError(response.status, code, message);
  }
  return payload as T;
}

export function jsonBody(value: unknown): Pick<RequestInit, "body"> {
  return { body: JSON.stringify(value) };
}

export function queryString(values: Record<string, string | number | boolean | undefined>): string {
  const query = new URLSearchParams();
  for (const [name, value] of Object.entries(values)) {
    if (value !== undefined && value !== "") query.set(name, String(value));
  }
  const encoded = query.toString();
  return encoded ? `?${encoded}` : "";
}

export async function uploadEvidence(file: File, evidenceType = "other", listingID = ""): Promise<string> {
	if (!file.type || !["image/png", "image/jpeg", "image/webp", "application/pdf"].includes(file.type)) {
		throw new Error(`不支持的附件类型：${file.name}`);
	}
	if (file.size < 1 || file.size > 20 * 1024 * 1024) {
		throw new Error(`附件 ${file.name} 必须小于 20MB`);
	}
	const digest = await crypto.subtle.digest("SHA-256", await file.arrayBuffer());
	const sha256 = Array.from(new Uint8Array(digest), (value) => value.toString(16).padStart(2, "0")).join("");
	const upload = await api<{ id: string; method: string; upload_url: string; headers: Record<string, string> }>("/api/v1/evidence/uploads", {
		method: "POST",
		...jsonBody({ listing_id: listingID || undefined, evidence_type: evidenceType, content_type: file.type, size_bytes: file.size, sha256 }),
	});
	const uploadURL = new URL(upload.upload_url, window.location.origin);
	const headers = new Headers(upload.headers);
	const sameOrigin = uploadURL.origin === window.location.origin;
	if (sameOrigin) {
		const token = getSessionToken();
		if (token) headers.set("Authorization", `Bearer ${token}`);
		headers.set("X-Request-ID", crypto.randomUUID());
	}
	const response = await fetch(uploadURL, {
		method: upload.method, headers, body: file, credentials: sameOrigin ? "same-origin" : "omit",
	});
	if (!response.ok) throw new Error(`附件 ${file.name} 上传失败（HTTP ${response.status}）`);
	const confirmed = await api<{ id: string }>(`/api/v1/evidence/uploads/${upload.id}/confirm`, { method: "POST" });
	return confirmed.id;
}

export async function openAuthenticatedResource(path: string, target?: Window | null): Promise<void> {
	const popup = target === undefined ? window.open("about:blank", "_blank") : target;
	if (popup) {
		popup.opener = null;
		popup.document.title = "附件加载中";
		popup.document.body.textContent = "附件加载中...";
		popup.document.body.style.cssText = "margin:0;display:grid;place-items:center;min-height:100vh;font:16px sans-serif;color:#344054;background:#f8fafc";
	}
	try {
		const resourceURL = new URL(path, window.location.origin);
		if (resourceURL.origin !== window.location.origin) throw new Error("附件地址不是平台同源地址");
		const headers = new Headers({ Accept: "image/png,image/jpeg,image/webp,application/pdf" });
		const token = getSessionToken();
		if (token) headers.set("Authorization", `Bearer ${token}`);
		headers.set("X-Request-ID", crypto.randomUUID());
		const response = await fetch(resourceURL, { headers, credentials: "same-origin" });
		if (!response.ok) {
			const contentType = response.headers.get("content-type") ?? "";
			const payload = contentType.includes("application/json") ? await response.json() as APIErrorBody : null;
			throw new Error(payload?.error?.message || `附件读取失败（HTTP ${response.status}）`);
		}
		const objectURL = URL.createObjectURL(await response.blob());
		if (!popup) {
			URL.revokeObjectURL(objectURL);
			throw new Error("浏览器拦截了附件窗口，请允许本站打开新窗口");
		}
		const frame = popup.document.createElement("iframe");
		frame.title = "附件预览";
		frame.src = objectURL;
		frame.style.cssText = "display:block;width:100vw;height:100vh;border:0;background:#fff";
		popup.document.title = "附件预览";
		popup.document.body.style.cssText = "margin:0;overflow:hidden;background:#fff";
		popup.document.body.replaceChildren(frame);
		window.setTimeout(() => URL.revokeObjectURL(objectURL), 5 * 60 * 1000);
	} catch (error) {
		if (popup) popup.close();
		throw error;
	}
}
